import { styled } from '@mui/material/styles';
import {
    Box, ButtonBase, Divider, Popover,
} from '@mui/material';
import { vCenteredFlex } from '../../foundation/Flex';

export const NavbarContainer = styled('div')`
    ${vCenteredFlex}
    padding: 1.5rem 0;
    justify-content: space-between;
    
    ${props => props.theme.breakpoints.down('md')} {
        padding: 1rem 0;
    }
`;

export const DesktopNavbar = styled(Box)`
    ${vCenteredFlex}
    gap: 1rem;
    
    ${props => props.theme.breakpoints.up('lg')} {
        gap: 2rem;
    }
`;

export const DesktopNavbarWrapper = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 1rem;
    flex: 1;
`;

export const DesktopContainer = styled(Box)`
    display: flex;
    width: 100%;
    align-items: center;
    gap: 1rem;
`;

export const MobileContainer = styled(Box)`
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: space-between;
`;

export const AuthButtons = styled(Box)`
    ${vCenteredFlex}
    gap: 1.75rem;
    margin-inline-start: 1rem;
    
    ${props => props.theme.breakpoints.up('md')} {
        justify-content: flex-start;
    }
`;

export const LogoContainer = styled(Box)`
    ${vCenteredFlex}
    gap: 1rem;
    
    ${props => props.theme.breakpoints.up('md')} {
        min-width: 200px;
        justify-content: flex-start;
    }
`;

export const LogoDivider = styled('span')(({ theme }) => ({
    backgroundColor: theme.palette.divider,
    display: 'inline-block',
    height: '2.1rem',
    width: 1,
    [theme.breakpoints.down('sm')]: {
        height: '1.8rem',
    },
}));

export const ExpressBadge = styled('span')(({ theme }) => ({
    alignItems: 'center',
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 8,
    color: theme.palette.primary.main,
    display: 'inline-flex',
    fontSize: '1rem',
    fontWeight: 700,
    justifyContent: 'center',
    lineHeight: 1,
    minHeight: '2.5rem',
    padding: '0.5rem 1.35rem',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
        fontSize: '0.875rem',
        minHeight: '2.2rem',
        padding: '0.45rem 1rem',
    },
}));

export const ExpressBadgeGroup = styled('span')(({ theme }) => ({
    alignItems: 'center',
    display: 'inline-flex',
    gap: '1rem',
    transform: 'translateY(0.6rem)',
    [theme.breakpoints.down('sm')]: {
        transform: 'translateY(0.35rem)',
    },
}));

export const ExpressLogoWrapper = styled('span')(({ theme }) => ({
    display: 'inline-flex',
    transform: 'translateY(-0.35rem)',
    [theme.breakpoints.down('sm')]: {
        transform: 'translateY(-0.2rem)',
    },
}));

export const RecruitButton = styled(Box)`
    white-space: nowrap;
    font-weight: 600;
`;

export const NavItemStyled = styled('div')<{ selected?: boolean }>`
    position: relative;
    a {
        cursor: pointer;
        color: ${props => (props.selected ? props.theme.palette.primary.main : props.theme.palette.secondary.main)};
        text-decoration: none;
        font-weight: 500;
        transition: all 0.2s ease;
        &:hover {
            text-decoration: none;
            color: ${props => props.theme.palette.primary.main};
            transform: translateY(-1px);
        }
    }
    svg {
        fill: ${props => props.theme.palette.primary.main};
        transition: fill 0.2s ease;
    }
    > svg {
        margin-inline-end: 0.8rem;
        margin-bottom: -0.5rem;
    }

    ${props => props.theme.breakpoints.up('md')} {
        ${vCenteredFlex}
        > div {
            max-height: 0;
            overflow: hidden;
        }
    }
    ${props => props.theme.breakpoints.down('md')} {
        padding-bottom: 1.5rem;
        a {
            color: ${props => props.theme.palette.secondary.main};
        }
    }
`;

export const SubMenuStyle = styled('div')`
    ${props => props.theme.breakpoints.down('md')} {
        margin-inline-start: 2rem;
        margin-top: 0.5rem;
        > div {
            display: flex;
            align-items: center;
            flex-direction: row-reverse;
            justify-content: flex-end;
            padding: 0.5rem 0;
        }
        > div.with-divider {
            border-top: 1px solid ${props => props.theme.palette.divider};
            margin-top: 0.5rem;
            padding-top: 1rem;
        }
        > div > svg:first-child {
            color: ${props => props.theme.palette.text.secondary};
            font-size: 1.25rem;
            margin-inline-end: 0.5rem;
        }
    }
    ${props => props.theme.breakpoints.up('md')} {
        inset-inline-start: -6px;
        display: flex;
        align-items: flex-start;
        flex-direction: column;
        background-color: #ffffff;
        width: 15rem;
        border-radius: 12px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        border: 1px solid #f0f0f0;
        > div {
            ${vCenteredFlex}
            cursor: pointer;
            a {
                color: ${props => props.theme.palette.secondary.main};
                font-weight: 400;
                transition: all 0.2s ease;
            }
            a:hover {
                color: ${props => props.theme.palette.primary.main};
                font-weight: 500;
            }
            width: 100%;
            padding: 0.75rem 1.5rem;
            transition: background 0.2s ease;
            &:hover {
                background: #f9f9fb;
            }
            > svg {
                margin-inline-start: auto;
                font-size: 1.2rem;
                opacity: 0.6;
            }
        }
        > div:first-of-type {
            margin-top: 0.5rem;
            border-radius: 12px 12px 0 0;
        }
        > div:last-child {
            margin-bottom: 0.5rem;
            border-radius: 0 0 12px 12px;
        }
    }
`;

export const AccountMenuTrigger = styled(ButtonBase)(({ theme }) => ({
    minHeight: 44,
    padding: theme.spacing(0.75, 2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 999,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.secondary.main,
    fontWeight: 600,
    transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
    '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.action.hover,
    },
    '&:focus-visible': {
        boxShadow: `0 0 0 3px ${theme.palette.primary.main}33`,
    },
    '& .MuiSvgIcon-root': {
        fontSize: 24,
    },
}));

export const AccountMenuChevron = styled('span', {
    shouldForwardProp: prop => prop !== '$open',
})<{ $open: boolean }>(({ theme, $open }) => ({
    display: 'inline-flex',
    marginInlineStart: theme.spacing(1),
    transform: $open ? 'rotate(180deg)' : 'none',
    transition: theme.transitions.create('transform'),
}));

export const AccountPopover = styled(Popover)(({ theme }) => ({
    '& .MuiPopover-paper': {
        minWidth: 240,
        marginTop: theme.spacing(1.25),
        overflow: 'visible',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius * 2,
        boxShadow: theme.shadows[4],
        '&::before': {
            content: '""',
            position: 'absolute',
            insetInlineEnd: theme.spacing(4),
            top: -7,
            width: 12,
            height: 12,
            transform: 'rotate(45deg)',
            borderInlineStart: `1px solid ${theme.palette.divider}`,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
        },
    },
}));

export const AccountMenuList = styled('div')(({ theme }) => ({
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 'inherit',
}));

export const AccountMenuItem = styled(ButtonBase)(({ theme }) => ({
    width: '100%',
    minHeight: 42,
    padding: theme.spacing(0.75, 1.25),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(1.25),
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.primary,
    textDecoration: 'none',
    fontSize: theme.typography.body1.fontSize,
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.primary.main,
    },
    '& .MuiSvgIcon-root': {
        color: theme.palette.text.secondary,
        fontSize: 21,
    },
}));

export const AccountMenuDivider = styled(Divider)(({ theme }) => ({
    margin: theme.spacing(0.75, 1),
}));
